import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Alert, Button, Card, Col, Empty, Progress, Row, Space, Spin, Table, Tag, Tooltip, Typography } from 'antd';
import { ArrowRightOutlined, ReloadOutlined, RightOutlined } from '@ant-design/icons';
import { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import * as api from '@apis/reservation-success';
import * as groupMonitoringApi from '@apis/group-monitoring';
import * as groupingAgentApi from '@apis/grouping-agent';
import * as lastChanceApi from '@apis/last-chance';
import { ReservationGrade, ReservationSuccessRiskSession } from '@apis/reservation-success';
import ReservationSuccessPageHeader from '@components/reservation-success/ReservationSuccessPageHeader';
import {
  RESERVATION_GRADE_COLOR,
  RESERVATION_GRADE_HINT,
  RESERVATION_GRADE_LABEL,
  RUN_STATUS_COLOR,
  alertTypeLabel,
  reservationGradeLabel,
  runStatusLabel,
} from '@utils/reservation-success-labels';

const { Text } = Typography;

const GRADE_ORDER: ReservationGrade[] = ['danger', 'caution', 'recommended', 'stable'];
const METRICS_DAYS = 30;

const formatTime = (value?: string | null) => (value ? dayjs(value).format('MM/DD HH:mm') : '-');
const formatRate = (rate?: number | null) => (rate == null ? '-' : `${rate.toFixed(1)}%`);

interface StageCardProps {
  step: number;
  title: string;
  description: string;
  to: string;
  loading?: boolean;
  children: React.ReactNode;
}

const StageCard: React.FC<StageCardProps> = ({ step, title, description, to, loading, children }) => {
  const navigate = useNavigate();
  return (
    <Card
      hoverable
      size="small"
      style={{ height: '100%' }}
      bodyStyle={{ display: 'flex', flexDirection: 'column', gap: 12, height: '100%' }}
      onClick={() => navigate(to)}>
      <Space style={{ justifyContent: 'space-between', width: '100%' }}>
        <Space size={8}>
          <Tag color="blue" style={{ marginInlineEnd: 0 }}>
            {step}
          </Tag>
          <Text strong>{title}</Text>
        </Space>
        <RightOutlined style={{ color: '#999' }} />
      </Space>
      <Text type="secondary" style={{ fontSize: 12 }}>
        {description}
      </Text>
      {loading ? <Spin size="small" /> : children}
    </Card>
  );
};

interface MetricLinkProps {
  label: string;
  value: React.ReactNode;
  to?: string;
  color?: string;
  tip?: string;
}

const MetricLink: React.FC<MetricLinkProps> = ({ label, value, to, color, tip }) => {
  const navigate = useNavigate();
  const content = (
    <div
      role={to ? 'link' : undefined}
      onClick={(e) => {
        if (!to) return;
        e.stopPropagation();
        navigate(to);
      }}
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'baseline',
        padding: '4px 6px',
        margin: '0 -6px',
        borderRadius: 4,
        cursor: to ? 'pointer' : 'default',
      }}
      className={to ? 'reservation-success-metric-link' : undefined}>
      <Text type="secondary">{label}</Text>
      <Text strong style={{ fontSize: 18, color }}>
        {value}
      </Text>
    </div>
  );
  return tip ? <Tooltip title={tip}>{content}</Tooltip> : content;
};

const ReservationSuccessDashboardPage: React.FC = () => {
  const navigate = useNavigate();

  const overviewQuery = useQuery({
    queryKey: ['reservation-success-overview'],
    queryFn: () => api.getReservationSuccessOverview({ days: 7 }),
    refetchInterval: 60000,
  });

  const groupSummaryQuery = useQuery({
    queryKey: ['reservation-success-dashboard-group-summary'],
    queryFn: () => groupMonitoringApi.getGroupMonitoringSummary({ dateType: 'upcoming' }),
    refetchInterval: 60000,
  });

  const groupingRunsQuery = useQuery({
    queryKey: ['reservation-success-dashboard-grouping-runs'],
    queryFn: () => groupingAgentApi.listGroupingAgentRuns({ limit: 1 }),
    refetchInterval: 60000,
  });

  const lastChanceQuery = useQuery({
    queryKey: ['reservation-success-dashboard-last-chance'],
    queryFn: () => lastChanceApi.getLastChanceSummary(),
    refetchInterval: 60000,
  });

  const metricsRange = {
    from: dayjs().subtract(METRICS_DAYS, 'day').format('YYYY-MM-DD'),
    to: dayjs().subtract(1, 'day').format('YYYY-MM-DD'),
    dDay: 3,
  };
  const metricsQuery = useQuery({
    queryKey: ['reservation-success-dashboard-metrics', metricsRange.from, metricsRange.to],
    queryFn: () => api.getReservationSuccessMetrics(metricsRange),
    staleTime: 10 * 60 * 1000,
  });

  const overview = overviewQuery.data?.data;
  const health = overview?.health;
  const sessions = overview?.sessions;
  const groupSummary = groupSummaryQuery.data?.data;
  const latestGroupingRun = groupingRunsQuery.data?.data?.[0];
  const lastChance = lastChanceQuery.data?.data;
  const metrics = metricsQuery.data?.data;

  const activeSessions = sessions ? GRADE_ORDER.reduce((sum, grade) => sum + (sessions.byGrade[grade] || 0), 0) : 0;
  const fillRate =
    lastChance && lastChance.activeRecruitmentCapacity > 0
      ? Math.round((lastChance.activePurchasedCount / lastChance.activeRecruitmentCapacity) * 100)
      : null;

  const refetchAll = () => {
    overviewQuery.refetch();
    groupSummaryQuery.refetch();
    groupingRunsQuery.refetch();
    lastChanceQuery.refetch();
    metricsQuery.refetch();
  };

  const isFetching =
    overviewQuery.isFetching ||
    groupSummaryQuery.isFetching ||
    groupingRunsQuery.isFetching ||
    lastChanceQuery.isFetching;

  const riskColumns: ColumnsType<ReservationSuccessRiskSession> = [
    {
      title: '등급',
      dataIndex: 'reservationGrade',
      width: 90,
      render: (grade: string) => <Tag color={RESERVATION_GRADE_COLOR[grade]}>{reservationGradeLabel(grade)}</Tag>,
    },
    {
      title: '수업일',
      dataIndex: 'sessionDate',
      width: 130,
      render: (value: string, row) => (
        <Space size={4}>
          <span>{dayjs(value).format('MM/DD (dd)')}</span>
          {row.dDay != null && <Text type="secondary">D-{row.dDay}</Text>}
        </Space>
      ),
    },
    { title: '파트너', dataIndex: 'partnerName', width: 140, ellipsis: true },
    {
      title: '상품 · 회차',
      key: 'product',
      ellipsis: true,
      render: (_, row) => (
        <Tooltip title={`${row.productName || ''} · ${row.optionName || ''}`}>
          <span>
            {row.productName || row.productId}
            {row.optionName && <Text type="secondary"> · {row.optionName}</Text>}
          </span>
        </Tooltip>
      ),
    },
    {
      title: '신청',
      dataIndex: 'applicantCount',
      width: 70,
      align: 'right',
      render: (v?: number) => (v != null ? `${v}명` : '-'),
    },
    {
      title: '종합진단',
      dataIndex: 'combinedDiagnosis',
      ellipsis: true,
      render: (v?: string) =>
        v ? (
          <Tooltip title={v}>
            <span>{v}</span>
          </Tooltip>
        ) : (
          '-'
        ),
    },
    {
      title: (
        <Tooltip title="수업 D-1~D-7, 조치권장 이상 등급이면 라스트찬스에 등록할 수 있습니다.">라스트찬스</Tooltip>
      ),
      dataIndex: 'lcEligible',
      width: 100,
      render: (v: boolean) => (v ? <Tag color="purple">등록 가능</Tag> : <Text type="secondary">-</Text>),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <style>{`.reservation-success-metric-link:hover { background: rgba(24, 144, 255, 0.08); }`}</style>
      <ReservationSuccessPageHeader
        isDashboard
        title="예약 성사 대시보드"
        description="그룹핑부터 성과까지 엔진의 현재 상태를 단계별로 보여 줍니다. 카드나 숫자를 누르면 해당 화면으로 이동합니다."
        extra={
          <Space>
            {overview?.hourBucket && <Text type="secondary">진단 기준 {overview.hourBucket.replace('T', ' ')}시</Text>}
            <Button icon={<ReloadOutlined />} onClick={refetchAll} loading={isFetching}>
              새로고침
            </Button>
          </Space>
        }
      />

      {overviewQuery.isError && (
        <Alert type="error" showIcon style={{ marginBottom: 16 }} message="대시보드 데이터를 불러오지 못했습니다." />
      )}

      {health && (
        <Space direction="vertical" style={{ width: '100%', marginBottom: 16 }}>
          {health.alerts.length === 0 ? (
            <Alert
              type="success"
              showIcon
              message={`엔진 정상 · 마지막 배치 성공 ${formatTime(health.lastSuccessfulRunAt)}`}
            />
          ) : (
            health.alerts.map((alert, i) => (
              <Alert
                key={i}
                type={alert.level === 'error' ? 'error' : 'warning'}
                showIcon
                message={alert.message}
                action={
                  <Button size="small" type="link" onClick={() => navigate('/reservation-success-batch-runs')}>
                    배치 실행 보기
                  </Button>
                }
              />
            ))
          )}
        </Space>
      )}

      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24} md={12}>
          <Card
            size="small"
            hoverable
            title="예약 성사 배치"
            extra={<Text type="secondary">매시 실행</Text>}
            onClick={() => navigate('/reservation-success-batch-runs')}
            loading={overviewQuery.isLoading}>
            <Row gutter={16}>
              <Col span={8}>
                <Text type="secondary">상태</Text>
                <div>
                  <Tag color={health?.healthy ? 'success' : 'error'}>{health?.healthy ? '정상' : '이상'}</Tag>
                </div>
              </Col>
              <Col span={8}>
                <Text type="secondary">최근 실행</Text>
                <div>
                  <Tag color={RUN_STATUS_COLOR[health?.lastRunStatus || ''] || 'default'}>
                    {health?.lastRunStatus === 'none' ? '기록 없음' : runStatusLabel(health?.lastRunStatus)}
                  </Tag>
                </div>
              </Col>
              <Col span={8}>
                <Text type="secondary">마지막 성공</Text>
                <div>
                  <Text strong>{formatTime(health?.lastSuccessfulRunAt)}</Text>
                </div>
              </Col>
            </Row>
          </Card>
        </Col>
        <Col xs={24} md={12}>
          <Card
            size="small"
            hoverable
            title="AI 그룹핑 에이전트"
            extra={<Text type="secondary">매일 실행</Text>}
            onClick={() =>
              navigate(latestGroupingRun ? `/grouping-agent-runs/${latestGroupingRun.runId}` : '/grouping-agent-runs')
            }
            loading={groupingRunsQuery.isLoading}>
            {latestGroupingRun ? (
              <Row gutter={16}>
                <Col span={8}>
                  <Text type="secondary">최근 실행</Text>
                  <div>
                    <Tag color={RUN_STATUS_COLOR[latestGroupingRun.status] || 'default'}>
                      {runStatusLabel(latestGroupingRun.status)}
                    </Tag>
                    {latestGroupingRun.dryRun && <Tag>미리보기</Tag>}
                  </div>
                </Col>
                <Col span={8}>
                  <Text type="secondary">실행 시각</Text>
                  <div>
                    <Text strong>{formatTime(latestGroupingRun.triggeredAt)}</Text>
                  </div>
                </Col>
                <Col span={8}>
                  <Text type="secondary">처리 회차</Text>
                  <div>
                    <Text strong>
                      {latestGroupingRun.summary?.processedScheduleCount ?? '-'} /{' '}
                      {latestGroupingRun.summary?.targetScheduleCount ?? '-'}
                    </Text>
                  </div>
                </Col>
              </Row>
            ) : (
              <Text type="secondary">실행 기록이 없습니다.</Text>
            )}
          </Card>
        </Col>
      </Row>

      <Text strong style={{ display: 'block', marginBottom: 8 }}>
        파이프라인 {overview ? `· 수업일 ${overview.range.from} ~ ${overview.range.to}` : ''}
      </Text>
      <Row gutter={[12, 12]} style={{ marginBottom: 24 }} wrap>
        <Col xs={24} sm={12} xl={4}>
          <StageCard
            step={1}
            title="그룹핑"
            description="결제건을 연령·인원 기준 그룹에 배정합니다."
            to="/group-monitoring"
            loading={groupSummaryQuery.isLoading}>
            <MetricLink
              label="배정 갭"
              value={groupSummary?.unassignedCount ?? '-'}
              color={groupSummary?.unassignedCount ? '#f69000' : undefined}
              tip="결제됐지만 그룹에 들어가지 않은 건"
              to="/group-monitoring?tab=unassigned"
            />
            <MetricLink
              label="보충 필요 그룹"
              value={groupSummary?.shortGroupCount ?? '-'}
              color={groupSummary?.shortGroupCount ? '#fc4242' : undefined}
              tip="최소 인원에 못 미친 그룹"
              to="/group-monitoring?tab=groups&groupStatus=short"
            />
            <MetricLink
              label="그룹 / 회차"
              value={`${groupSummary?.groupCount ?? '-'} / ${groupSummary?.optionCount ?? '-'}`}
              to="/group-monitoring?tab=options"
            />
          </StageCard>
        </Col>
        <Col xs={24} sm={12} xl={5}>
          <StageCard
            step={2}
            title="위험 감지"
            description="회차별 등급으로 환불·폐강 위험을 진단합니다."
            to="/reservation-success-product-indicators?sortField=danger&sortOrder=desc"
            loading={overviewQuery.isLoading}>
            {GRADE_ORDER.map((grade) => (
              <MetricLink
                key={grade}
                label={RESERVATION_GRADE_LABEL[grade]}
                value={sessions?.byGrade[grade] ?? 0}
                color={RESERVATION_GRADE_COLOR[grade]}
                tip={RESERVATION_GRADE_HINT[grade]}
                to={`/reservation-success-product-indicators?sortField=${grade}&sortOrder=desc`}
              />
            ))}
            {sessions && (
              <Text type="secondary" style={{ fontSize: 12 }}>
                진단 중 {activeSessions}회차 · 진단 전 {sessions.preDiagnosisSessions}회차
              </Text>
            )}
          </StageCard>
        </Col>
        <Col xs={24} sm={12} xl={5}>
          <StageCard
            step={3}
            title="파트너 알림"
            description="등급 변화와 라스트찬스 소식을 파트너에게 보냅니다."
            to="/reservation-success-alerts"
            loading={overviewQuery.isLoading}>
            <MetricLink
              label="오늘 발송"
              value={overview?.alerts.today.sent ?? 0}
              to={`/reservation-success-alerts?status=sent&dateFrom=${dayjs().format('YYYY-MM-DD')}`}
            />
            <MetricLink
              label="오늘 실패"
              value={overview?.alerts.today.failed ?? 0}
              color={overview?.alerts.today.failed ? '#fc4242' : undefined}
              to={`/reservation-success-alerts?status=failed&dateFrom=${dayjs().format('YYYY-MM-DD')}`}
            />
            <MetricLink
              label="최근 7일 전체"
              value={overview?.alerts.last7Days.total ?? 0}
              to="/reservation-success-alerts"
            />
            {!!overview?.alerts.byType7Days.length && (
              <Text type="secondary" style={{ fontSize: 12 }}>
                {overview.alerts.byType7Days
                  .slice(0, 3)
                  .map((row) => `${alertTypeLabel(row.alertType)} ${row.count}`)
                  .join(' · ')}
              </Text>
            )}
          </StageCard>
        </Col>
        <Col xs={24} sm={12} xl={5}>
          <StageCard
            step={4}
            title="라스트찬스 등록"
            description="위험 회차의 남은 자리를 특가로 다시 엽니다."
            to="/last-chance-monitoring"
            loading={overviewQuery.isLoading || lastChanceQuery.isLoading}>
            <MetricLink
              label="등록 가능 · 미등록"
              value={sessions?.lcEligibleOpen ?? 0}
              color={sessions?.lcEligibleOpen ? '#722ed1' : undefined}
              tip="라스트찬스 조건을 만족하지만 아직 등록하지 않은 회차"
              to="/reservation-success-product-indicators?sortField=danger&sortOrder=desc"
            />
            <MetricLink
              label="모집중"
              value={lastChance?.byDisplayStatus.active ?? 0}
              to="/last-chance-monitoring?displayStatus=active"
            />
            <MetricLink
              label="정원충족 · 편성완료"
              value={`${lastChance?.byDisplayStatus.fulfilled ?? 0} · ${lastChance?.byDisplayStatus.formed ?? 0}`}
              to="/last-chance-monitoring?displayStatus=formed"
            />
          </StageCard>
        </Col>
        <Col xs={24} sm={12} xl={5}>
          <StageCard
            step={5}
            title="고객 구매"
            description="모집 중인 라스트찬스의 구매 현황입니다."
            to="/last-chance-monitoring?displayStatus=active"
            loading={lastChanceQuery.isLoading}>
            <MetricLink
              label="구매 / 모집 정원"
              value={`${lastChance?.activePurchasedCount ?? 0} / ${lastChance?.activeRecruitmentCapacity ?? 0}`}
              tip="판매 중으로 등록된 라스트찬스 기준입니다. 마감일이 지나 모집종료로 보이는 상품도 포함합니다."
            />
            <div>
              <Text type="secondary" style={{ fontSize: 12 }}>
                좌석 채움
              </Text>
              <Progress percent={fillRate ?? 0} size="small" status={fillRate == null ? 'normal' : undefined} />
            </div>
            <MetricLink
              label="폐강완료"
              value={lastChance?.byDisplayStatus.cancelled ?? 0}
              to="/last-chance-monitoring?displayStatus=cancelled"
            />
          </StageCard>
        </Col>
      </Row>

      <Row gutter={[16, 16]}>
        <Col xs={24} xl={16}>
          <Card
            size="small"
            title="지금 확인할 회차"
            extra={
              <Button
                type="link"
                size="small"
                onClick={() => navigate('/reservation-success-product-indicators?sortField=danger&sortOrder=desc')}>
                상품 보조지표 전체 <ArrowRightOutlined />
              </Button>
            }>
            <Text type="secondary" style={{ display: 'block', marginBottom: 8, fontSize: 12 }}>
              7일 안에 수업하는 즉시대응·조치필요 회차를 가까운 날짜순으로 최대 10개 보여 줍니다. 행을 누르면 해당
              상품의 보조지표로 이동합니다.
            </Text>
            <Table<ReservationSuccessRiskSession>
              size="small"
              rowKey={(row) => `${row.productId}:${row.optionId}:${row.sessionDate}`}
              loading={overviewQuery.isLoading}
              dataSource={overview?.riskSessions || []}
              columns={riskColumns}
              pagination={false}
              tableLayout="fixed"
              scroll={{ x: 900 }}
              locale={{
                emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="대응이 필요한 회차가 없습니다." />,
              }}
              onRow={(row) => ({
                style: { cursor: 'pointer' },
                onClick: () =>
                  navigate(
                    `/reservation-success-product-indicators?keyword=${encodeURIComponent(row.productName || '')}`,
                  ),
              })}
            />
          </Card>
        </Col>
        <Col xs={24} xl={8}>
          <Card
            size="small"
            hoverable
            title="성과 지표"
            extra={<Text type="secondary">최근 {METRICS_DAYS}일 · D-3</Text>}
            onClick={() => navigate('/reservation-success-metrics')}>
            {metricsQuery.isLoading ? (
              <Spin size="small" />
            ) : metricsQuery.isError || !metrics ? (
              <Text type="secondary">지표를 불러오지 못했습니다.</Text>
            ) : (
              <>
                <MetricLink
                  label="감지 정확도"
                  value={formatRate(metrics.backtest.accuracy)}
                  tip="D-3 즉시대응 판정이 실제 전액환불 여부와 맞은 비율"
                />
                <MetricLink
                  label="미탐률"
                  value={formatRate(metrics.backtest.falseNegativeRate)}
                  tip="실제로 전액환불됐는데 즉시대응으로 보지 못한 비율"
                />
                <MetricLink
                  label="라스트찬스 전환율"
                  value={formatRate(metrics.clearMarket.conversion.rate)}
                  tip="모집이 끝난 라스트찬스 회차 중 1건 이상 판매된 비율"
                />
                <MetricLink
                  label="좌석 채움률"
                  value={formatRate(metrics.clearMarket.seatFill.rate)}
                  tip="라스트찬스 모집 정원 대비 구매 비율"
                />
                <MetricLink
                  label="전액환불률 변화"
                  value={`${formatRate(metrics.refund.before.rate)} → ${formatRate(metrics.refund.after.rate)}`}
                  tip="라스트찬스 등록 상품의 직전 같은 길이 기간 대비 이번 기간 전액환불률"
                />
              </>
            )}
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default ReservationSuccessDashboardPage;
